import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, ArrowRight, Tag, ShieldCheck, CreditCard, ChevronRight, Smartphone, Globe, Landmark, MapPin } from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useCart, cartStore } from "@/lib/cart";
import { createOrder, validateCoupon } from "@/lib/checkout.functions";
import { listEnabledProviders } from "@/lib/payments.functions";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const MOBILE_WALLETS = new Set(["bkash", "nagad", "rocket", "upay", "stcpay"]);

const REGION_META: Record<string, { label: string; icon: typeof MapPin }> = {
  sa: { label: "Saudi Arabia — Local & Mobile", icon: MapPin },
  bd: { label: "Bangladesh — Local & Mobile", icon: MapPin },
  international: { label: "International", icon: Globe },
  bank: { label: "Bank Transfer", icon: Landmark },
};

export const Route = createFileRoute("/checkout")({
  component: CheckoutPage,
  head: () => ({
    meta: [
      { title: "Checkout — CoderLity" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function CheckoutPage() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const placeOrder = useServerFn(createOrder);
  const checkCoupon = useServerFn(validateCoupon);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>("");

  const fetchProviders = useServerFn(listEnabledProviders);
  const { data: providers = [], isLoading: providersLoading } = useQuery({
    queryKey: ["enabled-payment-providers"],
    queryFn: () => fetchProviders({ data: {} }),
  });

  const grouped = useMemo(() => {
    const order = ["sa", "bd", "international", "bank"];
    const map: Record<string, any[]> = {};
    (providers as any[]).forEach((p) => {
      (map[p.region] ||= []).push(p);
    });
    return order.filter((r) => map[r]?.length).map((r) => ({ region: r, items: map[r] }));
  }, [providers]);

  useEffect(() => {
    if (!selectedProvider && (providers as any[]).length) {
      setSelectedProvider((providers as any[])[0].code);
    }
  }, [providers, selectedProvider]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setAuthed(!!data.user);
      if (data.user) {
        setEmail(data.user.email ?? "");
        setName((data.user.user_metadata as any)?.display_name ?? "");
      }
    });
  }, []);

  const subtotal_cents = Math.round(subtotal * 100);
  const total_cents = Math.max(0, subtotal_cents - discount);

  async function applyCoupon() {
    const code = coupon.trim();
    if (!code) return;
    try {
      const res = await checkCoupon({ data: { code, subtotal_cents } });
      setDiscount(res.discount_cents);
      setAppliedCoupon(res.code);
      toast.success(`Coupon ${res.code} applied`);
    } catch (e: any) {
      setDiscount(0);
      setAppliedCoupon(null);
      toast.error(e.message || "Invalid coupon");
    }
  }

  async function submit() {
    if (!items.length) return;
    if (!authed && (!name.trim() || !email.trim())) {
      toast.error("Name and email are required");
      return;
    }
    if (providers.length && !selectedProvider) {
      toast.error("Please select a payment method");
      return;
    }
    setSubmitting(true);
    try {
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const realItems = items.filter((i) => UUID_RE.test(i.id));
      const manualItems = items.filter((i) => !UUID_RE.test(i.id));

      if (!realItems.length) {
        toast.error("Custom items can't be checked out directly yet. Please add at least one marketplace product, or contact support to invoice your custom items.");
        setSubmitting(false);
        return;
      }

      const manualBlock = manualItems.length
        ? "\n\n--- Custom items ---\n" +
          manualItems
            .map((m) => `• ${m.name} × ${m.qty} @ $${m.price.toFixed(2)}${m.meta ? ` (${m.meta})` : ""}`)
            .join("\n")
        : "";

      const res = await placeOrder({
        data: {
          items: realItems.map((i) => ({ variant_id: i.id, qty: i.qty })),
          guest: authed ? undefined : { name: name.trim(), email: email.trim(), phone: phone.trim() || undefined },
          coupon_code: appliedCoupon || undefined,
          notes: (notes.trim() + manualBlock).slice(0, 2000) || undefined,
        },
      });
      cartStore.clear();
      toast.success("Order placed!");
      navigate({ to: "/orders/$orderId", params: { orderId: res.order_id } });
    } catch (e: any) {
      toast.error(e.message || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  }

  if (!items.length) {
    return (
      <PageShell>
        <PageHero eyebrow="Checkout" title="Your cart is empty" sub="Add a product before checking out." />
        <div className="py-12 text-center">
          <Link to="/marketplace" className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-white shadow-glow">
            Browse marketplace <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHero eyebrow="Checkout" title="Secure checkout" sub="Review your details and place the order." />
      <section className="py-10">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <nav className="mb-6 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/cart" className="hover:text-foreground">Cart</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">Checkout</span>
          </nav>

          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <h2 className="text-sm font-semibold mb-4">Contact information</h2>
                {authed === false && (
                  <p className="mb-4 text-xs text-muted-foreground">
                    Already a customer? <Link to="/login" className="text-primary hover:underline">Sign in</Link> for faster checkout.
                  </p>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Full name *</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" required />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" required disabled={authed === true} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="notes">Order notes (optional)</Label>
                    <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" rows={3} />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card p-6">
                <h2 className="text-sm font-semibold mb-4">Items</h2>
                <ul className="divide-y divide-border">
                  {items.map((it) => (
                    <li key={it.id} className="flex items-start justify-between gap-3 py-3 text-sm">
                      <div>
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-muted-foreground">Qty {it.qty} × ${it.price.toFixed(2)}</div>
                      </div>
                      <div className="font-semibold">${(it.price * it.qty).toFixed(2)}</div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl border border-border bg-card p-6">
                <h2 className="text-sm font-semibold mb-1 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" /> Payment method
                </h2>
                <p className="text-xs text-muted-foreground mb-4">
                  Choose how you'd like to pay. Local cards, mobile wallets, and international options are supported.
                </p>
                {providersLoading ? (
                  <p className="text-sm text-muted-foreground">Loading payment methods…</p>
                ) : grouped.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                    No payment gateways are enabled yet. An admin can enable them in
                    <span className="font-medium text-foreground"> Admin → Payment Gateways</span>.
                  </div>
                ) : (
                  <div className="space-y-5">
                    {grouped.map(({ region, items: opts }) => {
                      const meta = REGION_META[region] ?? { label: region, icon: CreditCard };
                      const RegionIcon = meta.icon;
                      return (
                        <div key={region}>
                          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            <RegionIcon className="h-3 w-3" /> {meta.label}
                          </div>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {opts.map((p: any) => {
                              const isMobile = MOBILE_WALLETS.has(p.code);
                              const active = selectedProvider === p.code;
                              return (
                                <button
                                  type="button"
                                  key={p.code}
                                  onClick={() => setSelectedProvider(p.code)}
                                  className={cn(
                                    "flex items-start gap-3 rounded-lg border p-3 text-left transition",
                                    active
                                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                                      : "border-border bg-card hover:bg-secondary/50",
                                  )}
                                >
                                  <span className={cn(
                                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                                    active ? "bg-gradient-brand text-white shadow-glow" : "bg-secondary text-muted-foreground",
                                  )}>
                                    {isMobile ? <Smartphone className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-sm font-semibold truncate">{p.display_label || p.display_name}</span>
                                      {p.mode === "test" && (
                                        <Badge variant="secondary" className="text-[9px] px-1 py-0">TEST</Badge>
                                      )}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                      {(p.supported_currencies ?? []).join(" • ") || (isMobile ? "Mobile wallet" : "Card / bank")}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>


            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                <div className="rounded-xl border border-border bg-card p-5">
                  <h2 className="text-sm font-semibold mb-4">Order summary</h2>

                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                      <Tag className="h-3 w-3" /> Coupon code
                    </label>
                    <div className="flex gap-2">
                      <Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="WELCOME10" className="h-9" />
                      <Button onClick={applyCoupon} variant="outline" size="sm" className="h-9">Apply</Button>
                    </div>
                  </div>

                  <Separator className="my-3" />
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Subtotal</dt>
                      <dd>${(subtotal_cents / 100).toFixed(2)}</dd>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-emerald-500">
                        <dt>Discount</dt>
                        <dd>−${(discount / 100).toFixed(2)}</dd>
                      </div>
                    )}
                  </dl>
                  <Separator className="my-3" />
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="text-2xl font-bold text-gradient">${(total_cents / 100).toFixed(2)}</span>
                  </div>

                  <Button
                    onClick={submit}
                    disabled={submitting}
                    className="mt-4 w-full bg-gradient-brand text-white shadow-glow"
                    size="lg"
                  >
                    <Lock className="mr-2 h-4 w-4" /> {submitting ? "Placing order…" : "Place order"}
                  </Button>

                  <div className="mt-4 flex flex-col gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> 30-day money-back guarantee</div>
                    <div className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5 text-primary" /> Payment provider activates next</div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
